import classNames from 'classnames';
import React, { FC } from 'react';
import { useTranslation } from 'next-i18next';

import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import { ListingCardLocation } from 'src/common/types/common/ListingCard';
import { EXTERNAL_PATHS, PATHS } from '../../constants';
import getClassNames from '../../utils/getClassNames';
import { useFavoritesContext } from '../../providers/FavoritesProvider';
import { useUserContext } from '../../providers/UserProvider';
import IconDS22, { type IconProps } from '../IconDS22';
import UniversalLink from '../UniversalLink';

import styles from './ListingFavoriteButton.module.scss';

export type ListingFavoriteButtonProps = {
  listingId: string;
  isHighlighted?: boolean;
  cardLocation?: ListingCardLocation;
  iconSize?: IconProps['size'];
};

const ListingFavoriteButton: FC<ListingFavoriteButtonProps> = ({
  listingId,
  isHighlighted,
  cardLocation,
  iconSize,
}) => {
  const isAuthMigrationEnabled = useFeatureFlag('auth-migration');
  const REGISTER = isAuthMigrationEnabled ? PATHS.REGISTER : EXTERNAL_PATHS.REGISTER;
  const [t] = useTranslation('common');
  const { user } = useUserContext();
  const { toggleFavorite, hasFavorite, canEditFavorites } = useFavoritesContext();

  const isFavorite = hasFavorite(listingId);
  const modifier = classNames({ isFavorite });
  const ariaLabel = t('favorite-button--favorite-aria-label');

  if (!user?.id) {
    return (
      <UniversalLink
        href={REGISTER}
        className={getClassNames(styles, 'root', `${modifier} link`)}
        aria-label={ariaLabel}
      >
        <IconDS22
          id={isFavorite ? 'heart-filled' : 'heart'}
          size={iconSize ?? 'l'}
        />
      </UniversalLink>
    );
  }

  return (
    <button
      type="button"
      disabled={!canEditFavorites}
      className={getClassNames(styles, 'root', modifier)}
      onClick={() => {
        toggleFavorite(listingId, isHighlighted, cardLocation);
      }}
      aria-label={ariaLabel}
      aria-pressed={isFavorite ? 'true' : 'false'}
    >
      <IconDS22
        id={isFavorite ? 'heart-filled' : 'heart'}
        size={iconSize ?? 'l'}
      />
    </button>
  );
};

export default ListingFavoriteButton;
